import { useCallback, useEffect, useMemo, useState } from "react"

import {
  Box,
  ButtonBase,
  SvgIcon as MUISvgIcon,
  Stack,
  Typography,
  type ButtonBaseProps,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"

import { FCWC, Link, SvgIcon, type FC, type iconKeys } from ".."
import { BlogSubNavigationProps, type NavigationItemProps } from "./Types"

const BlogSubNavigation: FC<BlogSubNavigationProps> = ({ title, items, slug }) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const startIndex = useMemo(() => {
    const currentIndex = items?.findIndex((element) => element?.uri === slug)
    return currentIndex !== -1 ? Math.floor((currentIndex + 1) / 5) : 0
  }, [items, slug])

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    slidesToScroll: 5,
    startIndex: startIndex,
  })

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (emblaApi) {
        setPrevBtnDisabled(!emblaApi.canScrollPrev())
        setNextBtnDisabled(!emblaApi.canScrollNext())
      }
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi])

  return (
    <Stack
      width="100%"
      direction={{ xs: "column", lg: "row" }}
      position="relative"
      minHeight={120}
      sx={(theme) => ({
        background: theme.palette["tertiary.200"].main,
        padding: { xs: 0, lg: theme.spacing(6, 10) },
      })}
    >
      <NavigationHeading title={title} />
      <Box minWidth={{ lg: 810 }} position="relative">
        <Stack
          alignItems="flex-start"
          justifyContent="center"
          width={{ xs: "auto", lg: 40 }}
          sx={(theme) => ({
            background: {
              lg: `linear-gradient(-90deg, ${alpha(theme.palette["tertiary.200"].main, 0)} 0%, ${
                theme.palette["tertiary.200"].main
              } 50%)`,
            },
            position: "absolute",

            left: 0,
            top: 0,
            height: "100%",
            width: 100,
            zIndex: 10,
          })}
        >
          {!prevBtnDisabled && (
            <CarouselBtn aria-label="scroll previous" onClick={() => scrollPrev()}>
              <SvgIcon
                variant="arrow"
                size={12}
                sx={{
                  fill: (theme) => theme.palette["neutral.700"].main,
                  background: (theme) => theme.palette["neutral.100"].main,
                  transform: (theme) =>
                    theme.transitions.create("fill", {
                      duration: theme.transitions.duration.standard,
                    }),
                }}
              />
            </CarouselBtn>
          )}
        </Stack>
        <Box ref={emblaRef} overflow="hidden" position="relative" pr={{ xs: 5, lg: 0 }}>
          <Box
            display="grid"
            gridAutoFlow="column"
            gridAutoColumns="max-content"
            alignItems="center"
            maxWidth={{ lg: 610 }}
            width="100%"
            columnGap={{ xs: 7.5, lg: 15 }}
            p={(theme) => ({ xs: theme.spacing(4, 5, 3), lg: 0 })}
            ml={{ lg: 25 }}
          >
            {items?.map((category, i) => (
              <NavigationCategoryItem key={i} isSelected={slug === category?.uri} {...category} />
            ))}
          </Box>
        </Box>
        <Stack
          alignItems="flex-end"
          justifyContent="center"
          width={{ xs: "auto", lg: 40 }}
          sx={(theme) => ({
            background: {
              lg: `linear-gradient(90deg, ${alpha(theme.palette["tertiary.200"].main, 0)} 0%, ${
                theme.palette["tertiary.200"].main
              } 50%)`,
            },
            position: "absolute",

            right: 0,
            top: 0,
            height: "100%",
            width: 100,
            zIndex: 10,
          })}
        >
          {!nextBtnDisabled && (
            <CarouselBtn aria-label="scroll next" onClick={() => scrollNext()}>
              <SvgIcon
                variant="arrow"
                size={12}
                sx={{
                  fill: (theme) => theme.palette["neutral.700"].main,
                  background: (theme) => theme.palette["neutral.100"].main,
                  rotate: "180deg",
                  transform: (theme) =>
                    theme.transitions.create("fill", {
                      duration: theme.transitions.duration.standard,
                    }),
                }}
              />
            </CarouselBtn>
          )}
        </Stack>
      </Box>
    </Stack>
  )
}

export default BlogSubNavigation

/** A sub-component */
const NavigationHeading: FC<Pick<BlogSubNavigationProps, "title">> = ({ title }) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={5}
      width="100%"
      padding={(theme) => ({ xs: theme.spacing(4.5, 5), lg: 0 })}
      borderBottom={(theme) => ({ xs: `1px solid ${theme.palette["neutral.200"].main}`, lg: 0 })}
    >
      <Box
        display="flex"
        position="relative"
        pr={5}
        sx={{
          borderRight: (theme) => `1px solid ${alpha(theme.palette["neutral.700"].main, 0.2)}`,
        }}
      >
        <Link type="overlay" href="/inspiration" aria-label={title} />
        <MUISvgIcon viewBox="0 0 26 36" sx={{ width: 26, height: 36 }}>
          <path
            d="M11.6195 20.324C6.01358 20.0998 4.077 19.2436 4.34201 16.7566C6.01358 17.4905 8.21516 17.9186 11.0079 17.9186C18.4484 17.9186 22.0362 14.6978 22.0362 10.2131C22.0362 7.80763 20.9966 5.62643 18.7746 4.15871V4.13832C18.6319 4.0364 18.4688 3.95486 18.3057 3.85293C18.265 3.83255 18.2242 3.81216 18.1834 3.79178C18.1223 3.75101 18.0611 3.73062 18 3.68985C16.8992 3.11907 15.5538 2.69099 13.9841 2.44637H13.9637C13.8007 2.42598 13.6376 2.4056 13.4745 2.38521C13.4337 2.38521 13.393 2.38521 13.3522 2.36483C13.2095 2.34444 13.0668 2.34444 12.9241 2.32406C12.8426 2.32406 12.7814 2.30367 12.6999 2.30367C12.5776 2.30367 12.4552 2.28329 12.3329 2.28329C12.2106 2.28329 12.1087 2.2629 11.9864 2.2629C11.8845 2.2629 11.8029 2.2629 11.701 2.24252C11.4768 2.24252 11.2729 2.24252 11.0487 2.24252C3.4043 2.22213 0 5.85067 0 10.1927C0 12.7 1.03964 14.7997 3.28199 16.1859C1.50849 17.0013 0 18.6321 0 20.8744C0 24.6253 3.22083 25.9911 9.09172 26.4191C13.3522 26.7249 17.9184 27.153 17.9184 30.2923C17.9184 33.2277 14.5549 34.512 11.3341 34.5935C7.11437 34.7158 3.62853 31.6173 6.17666 27.3976C2.40543 27.6015 0 29.1507 0 31.1281C0 34.0228 4.13816 36.0001 11.1098 36.0001C19.9365 36.0001 24.8086 32.6366 24.8086 27.2957C24.8086 21.9548 19.5085 20.6502 11.6195 20.324ZM11.0283 3.60831C14.0453 3.60831 15.1665 6.58453 15.1665 9.98882C15.1665 13.5562 14.0453 16.5324 11.0283 16.5324C7.92977 16.5324 6.89014 13.5562 6.89014 9.98882C6.89014 6.58453 7.92977 3.60831 11.0283 3.60831Z"
            fill="#F05B2A"
          />
          <path
            d="M23.7282 6.64552L25.624 0L18.9173 1.65119L22.2605 3.32276L23.7282 6.64552Z"
            fill="#727272"
          />
        </MUISvgIcon>
      </Box>
      <Box>
        <Typography variant="body.lg" component="h2">{title}</Typography>
      </Box>
    </Stack>
  )
}

/** A sub-component for the Blog sub navigation category */
const NavigationCategoryItem: FC<
  NavigationItemProps & ButtonBaseProps & { isSelected: boolean }
> = ({ title, icon, uri, isSelected }) => {
  return (
    <Box
      position="relative"
      py={{ xs: 2, lg: 3 }}
      sx={(theme) => ({
        borderBottom: isSelected
          ? `1px solid ${theme.palette["primary.800"].main}`
          : `1px solid ${theme.palette["tertiary.200"].main}`,
      })}
    >
      <Link type="overlay" href={uri} aria-label={title} />
      <Stack direction="column" alignItems="center" rowGap={{ xs: 3, lg: 4 }}>
        <SvgIcon
          variant={icon as iconKeys}
          viewBox="0 0 30 30"
          sx={{ fill: (theme) => theme.palette["neutral.700"].main }}
        />
        <Typography variant="body.sm" component="h2" fontWeight={500} sx={{ lineHeight: "100%" }}>
          {title}
        </Typography>
      </Stack>
    </Box>
  )
}

/** A sub-component for the Carousel button */
const CarouselBtn: FCWC<ButtonBaseProps> = ({ children, onClick, ...props }) => {
  return (
    <Box
      component={ButtonBase}
      p={4}
      sx={(theme) => ({
        "pointerEvents": "all",
        "borderRadius": theme.spacing(25),
        "border": `1px solid ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
        "transition": theme.transitions.create("background-color"),
        "width": { xs: 40 },
        "height": { xs: 40 },

        "&:hover": {
          bgcolor: theme.palette["neutral.100"].main,
        },
        [theme.breakpoints.down("lg")]: {
          display: "none",
        },
      })}
      onClick={onClick}
      {...props}
    >
      {children}
    </Box>
  )
}
